/**
 * LineRow — rendu d'une ligne de devis (3 variantes : standard / note / option).
 *
 * Phase 3e — Refonte grille (Bugs 6 & 7)
 *
 * Grille CSS explicite en pixels (pas de %) pour lisibilité prévisible sur tous devices :
 *   Mobile / iPad portrait (< 1024) → stack complet (1 col), chaque champ pleine largeur.
 *   Desktop / iPad landscape (≥ 1024) → 6 colonnes fixes :
 *     Description flex-grow | Qté 88px | Unité 116px | PU 140px | TVA 88px | HT 140px
 *
 * Rationale largeurs :
 *   - Qté 88px : accueille "999,99" en tabular-nums (~ 60px + padding).
 *   - Unité 116px : accueille "pièce(s)" (le libellé prédéfini le plus long) + chevron dropdown.
 *   - PU HTVA 140px : accueille "999 999,99 €" en tabular-nums (~ 100px + padding).
 *   - TVA 88px : affiche libellé COURT en état fermé ("6 %" / "21 %" / "0 % CC") + chevron.
 *     Les items dropdown gardent leur libellé long ("0 % Cocontractant") pour clarté à la sélection.
 *   - HT 140px : miroir de PU HTVA pour cohérence visuelle.
 */
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { GripVertical, Trash2, StickyNote, Package } from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";
import BENumericInput from "@/components/quote/BENumericInput";
import UnitCombobox from "@/components/quote/UnitCombobox";

const VAT_OPTIONS = [
  { key: "6:standard", label: "6 %", rate: 6, regime: "standard" },
  { key: "21:standard", label: "21 %", rate: 21, regime: "standard" },
  { key: "0:cocontractant", label: "0 % Cocontractant", rate: 0, regime: "cocontractant" },
];

function vatKey(line) {
  if (line.vat_regime === "cocontractant" || line.vat_rate === 0) return "0:cocontractant";
  return `${line.vat_rate}:standard`;
}

// Libellé court affiché dans le SelectTrigger (état fermé) — tient dans 88px de colonne
function shortVatLabel(line) {
  if (line.vat_regime === "cocontractant" || line.vat_rate === 0) return "0 % CC";
  return `${line.vat_rate} %`;
}

// Classe CSS commune pour les champs numériques tabulaires (nbsp/virgule/chiffres alignés)
const NUM_INPUT_CLASS =
  "tabular border-slate-700 bg-slate-950/40 text-right text-slate-100 w-full";

// Classe grille — 1 col en mobile/tablet portrait, 6 col fixes en lg (≥ 1024)
const GRID_CLASS =
  "grid gap-2 grid-cols-1 lg:[grid-template-columns:minmax(240px,1fr)_88px_116px_140px_88px_140px]";

export default function LineRow({ line, idx, isDraft, onUpdate, onRemove, onAddToCatalog, dragHandleProps, dragging }) {
  const ht = (Number(line.quantity) || 0) * (Number(line.unit_price) || 0);
  const isCocontractant = line.vat_regime === "cocontractant" || line.vat_rate === 0;

  const DragHandle = dragHandleProps ? (
    <button
      {...dragHandleProps}
      className="cursor-grab rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
      title="Déplacer (drag)"
      data-testid={`line-drag-${idx}`}
      aria-label="Déplacer la ligne"
    >
      <GripVertical className="h-3.5 w-3.5" />
    </button>
  ) : null;

  // ─── Ligne "note" : texte libre uniquement, pas de grille ────────────────
  if (line.is_note) {
    return (
      <div
        className={`rounded border border-slate-700/60 bg-slate-800/40 p-2 italic ${dragging ? "opacity-40" : ""}`}
        data-testid={`quote-line-${idx}`}
      >
        <div className="mb-1 flex items-center gap-2">
          {isDraft && DragHandle}
          <StickyNote className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Note</span>
          {isDraft && (
            <button onClick={onRemove} className="ml-auto rounded p-1 text-red-400 hover:bg-red-500/10" data-testid={`line-remove-${idx}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
        <Textarea
          rows={2}
          value={line.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          disabled={!isDraft}
          placeholder="Texte informatif affiché sans montant (ex. contrainte de chantier, précision technique)…"
          className="border-slate-700 bg-slate-950/40 italic text-slate-100"
          data-testid={`line-description-${idx}`}
        />
      </div>
    );
  }

  // ─── Ligne "option" OU ligne standard ────────────────────────────────────
  const isOption = !!line.is_option;
  return (
    <div
      className={`rounded border p-3 ${
        isOption
          ? "border-[#D99A2B]/60 bg-[#D99A2B]/5"
          : "border-slate-800 bg-slate-950/30"
      } ${dragging ? "opacity-40" : ""}`}
      data-testid={`quote-line-${idx}`}
    >
      {/* Header : drag + badges + remove */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {isDraft && DragHandle}
        {isOption && (
          <div className="inline-flex items-center gap-1 rounded bg-[#D99A2B]/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#F0C572]">
            <Package className="h-3 w-3" /> Option (hors total)
          </div>
        )}
        {isCocontractant && !isOption && (
          <div
            className="inline-flex items-center gap-1 rounded bg-[#5A6338]/25 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[#C4D68A]"
            data-testid={`line-vat-cocontractant-badge-${idx}`}
          >
            Cocontractant · Autoliquidation
          </div>
        )}
        {line.catalog_item_id && (
          <span className="inline-block rounded bg-[#7A8450]/15 px-1.5 py-0.5 text-[10px] text-[#B8C784]">
            catalogue
          </span>
        )}
        {!line.catalog_item_id && !isOption && (
          <span
            className="inline-flex items-center gap-1 rounded border border-[#D99A2B]/40 bg-[#D99A2B]/10 px-1.5 py-0.5 text-[10px] text-[#F0C572]"
            data-testid={`line-off-catalog-badge-${idx}`}
          >
            Hors catalogue — prix à vérifier
          </span>
        )}
        {!line.catalog_item_id && !isOption && isDraft && onAddToCatalog && (
          <button
            onClick={() => onAddToCatalog(line)}
            className="inline-flex items-center gap-1 rounded border border-[#C4553B]/40 bg-[#C4553B]/10 px-1.5 py-0.5 text-[10px] text-[#F0C572] hover:bg-[#C4553B]/20"
            data-testid={`line-add-to-catalog-${idx}`}
            title="Ajouter cette ligne au catalogue"
          >
            + Ajouter au catalogue
          </button>
        )}
        {line.needs_review && (
          <span
            className="inline-block rounded bg-[#D99A2B]/15 px-1.5 py-0.5 text-[10px] text-[#F0C572]"
            title={line.review_reason || ""}
          >
            à vérifier
          </span>
        )}
        {isDraft && (
          <button
            onClick={onRemove}
            className="ml-auto rounded p-1 text-red-400 hover:bg-red-500/10"
            data-testid={`line-remove-${idx}`}
            aria-label="Supprimer la ligne"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Grille des champs */}
      <div className={GRID_CLASS}>
        {/* Description — full width sur mobile, flex-grow sur desktop */}
        <div className="min-w-0">
          <Label className="text-[11px] text-slate-500">Description</Label>
          <Textarea
            rows={2}
            value={line.description}
            onChange={(e) => onUpdate({ description: e.target.value })}
            disabled={!isDraft}
            className="w-full border-slate-700 bg-slate-950/40 text-sm text-slate-100"
            data-testid={`line-description-${idx}`}
          />
        </div>

        {/* Qté — 88px */}
        <div className="min-w-0">
          <Label className="text-[11px] text-slate-500">Qté</Label>
          <BENumericInput
            value={line.quantity}
            onChange={(n) => onUpdate({ quantity: n })}
            disabled={!isDraft}
            className={NUM_INPUT_CLASS}
            data-testid={`line-quantity-${idx}`}
          />
        </div>

        {/* Unité — 116px pour "pièce(s)" + chevron */}
        <div className="min-w-0">
          <Label className="text-[11px] text-slate-500">Unité</Label>
          <UnitCombobox
            value={line.unit}
            onChange={(v) => onUpdate({ unit: v })}
            disabled={!isDraft}
            className="w-full"
            data-testid={`line-unit-${idx}`}
          />
        </div>

        {/* PU HTVA — 140px pour "999 999,99 €" */}
        <div className="min-w-0">
          <Label className="text-[11px] text-slate-500">PU HTVA</Label>
          <BENumericInput
            value={line.unit_price}
            onChange={(n) => onUpdate({ unit_price: n })}
            disabled={!isDraft}
            className={NUM_INPUT_CLASS}
            data-testid={`line-unit-price-${idx}`}
          />
        </div>

        {/* TVA — 88px (libellé court "0 % CC" en état fermé) */}
        <div className="min-w-0">
          <Label className="text-[11px] text-slate-500">TVA</Label>
          <Select
            value={vatKey(line)}
            onValueChange={(v) => {
              const opt = VAT_OPTIONS.find((o) => o.key === v);
              if (opt) onUpdate({ vat_rate: opt.rate, vat_regime: opt.regime });
            }}
            disabled={!isDraft}
          >
            <SelectTrigger
              className="w-full border-slate-700 bg-slate-950/40 text-xs"
              data-testid={`line-vat-${idx}`}
            >
              <span className="truncate">{shortVatLabel(line)}</span>
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900">
              {VAT_OPTIONS.map((o) => (
                <SelectItem
                  key={o.key}
                  value={o.key}
                  data-testid={`line-vat-option-${o.rate}-${idx}`}
                >
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Total HT — 140px pour "999 999,99 €" */}
        <div className="min-w-0">
          <Label className="text-[11px] text-slate-500">HT</Label>
          <div
            className={`tabular w-full overflow-hidden truncate rounded border px-2 py-2 text-right text-sm font-semibold ${
              isOption
                ? "border-[#D99A2B]/40 bg-[#D99A2B]/5 text-[#F0C572]"
                : "border-slate-800 bg-slate-950/60 text-slate-100"
            }`}
            data-testid={`line-total-ht-${idx}`}
            title={formatCurrencyBE(ht)}
          >
            {isOption ? `(${formatCurrencyBE(ht)})` : formatCurrencyBE(ht)}
          </div>
        </div>
      </div>
    </div>
  );
}
